// UP6 (GOALS/GOALS-UPSTREAM-PARITY.html#UP6): the Desk's side of session
// notifications. The pure fold and the event contract live in
// @cleo/contracts (packages/contracts/src/sessionNotifications.ts) so Pocket
// codes its Android ongoing activity against the same shape; this module
// re-exports them and adds the Desk-only words and channel predicates.
import type { SessionNotificationKind, SessionNotificationMode } from "@cleo/contracts";

export {
  detectSessionNotifications,
  markOf,
  sessionNotificationKey,
  type DetectSessionNotificationsInput,
  type DetectSessionNotificationsResult,
  type SessionNotificationEvent,
  type SessionNotificationKind,
  type SessionNotificationMark,
  type SessionNotificationRow,
} from "@cleo/contracts";

export const hasSystemNotifications = (mode: SessionNotificationMode): boolean =>
  mode === "system" || mode === "system-and-sound";
export const hasNotificationSound = (mode: SessionNotificationMode): boolean =>
  mode === "sound" || mode === "system-and-sound";

export const SESSION_NOTIFICATION_TITLES: Readonly<Record<SessionNotificationKind, string>> = {
  completed: "Session finished",
  failed: "Session failed",
  input: "Session needs an answer",
  approval: "Session needs an approval",
};

export const SESSION_NOTIFICATION_MODE_LABELS: Readonly<Record<SessionNotificationMode, string>> = {
  off: "Off",
  system: "System notification",
  sound: "Sound only",
  "system-and-sound": "System notification with sound",
};

/** Which bundled sound an event plays: completion uses the chime, attention the two-tone. */
export const soundKindForEvent = (kind: SessionNotificationKind): "completion" | "attention" =>
  kind === "completed" ? "completion" : "attention";

/**
 * Whether the Desk is in front of the person right now. A visible, focused
 * document takes the in-app notice; anything else takes the system channel.
 */
export const deskIsFocused = (doc: Pick<Document, "visibilityState" | "hasFocus">): boolean =>
  doc.visibilityState === "visible" && doc.hasFocus();
